import React, { useMemo } from 'react'

import classnames from 'classnames'

import SVG from 'assets/svg/_SVG'

//  ///////////////////////
//  SECTION: TYPES
export type TThemeInputBaseProps = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> &
  React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>

export interface IThemeInputCounter {
  current: number
  max: number
  warning?: boolean
  label: string
}

export interface IThemeInputProps extends TThemeInputBaseProps {
  id?: string
  className?: string

  inputType?: 'input' | 'textarea'

  name: string
  touched?: boolean
  error?: string
  valid?: boolean
  counter?: IThemeInputCounter

  disabled?: boolean
  loading?: boolean
}

//  ///////////////////////
//  SECTION: EXPORTS
const Input: React.FC<IThemeInputProps> = ({
  children,
  id,
  className,
  inputType = 'input',
  touched,
  error,
  valid,
  counter,
  ...props
}) => {
  const { required, disabled, loading } = props

  const placeholder = useMemo<string | undefined>(() => {
    if (!props.placeholder) return undefined
    if (!props.required) return props.placeholder
    return `${props.placeholder} *`
  }, [props.placeholder, props.required])

  const hasError = Boolean(touched && error)
  const isValid = Boolean(valid && !hasError)

  const elementClassName = classnames(
    'tInput',
    {
      'tInput--filled': props.value,
      'tInput--error': hasError,
      'tInput--valid': isValid,
      'tInput--touched': touched,
      'tInput--required': required,
      'tInput--disabled': disabled,
      'tInput--loading': loading,
    },
    className
  )

  const subElementClassName = classnames('tInput__group', {
    'tInput__group--filled': props.value,
    'tInput__group--error': hasError,
    'tInput__group--valid': isValid,
    'tInput__group--touched': touched,
    'tInput__group--required': required,
    'tInput__group--disabled': disabled,
    'tInput__group--loading': loading,
  })

  return (
    <>
      <div id={id} className={elementClassName}>
        <div className={subElementClassName}>
          {inputType === 'input' && <input {...props} placeholder={placeholder} />}
          {inputType === 'textarea' && <textarea {...props} placeholder={placeholder} />}
          {hasError && <SVG className='tInput__group__error' title='input.error' />}
        </div>
        {counter && (
          <small
            id={`${props.name}-counter`}
            aria-live='polite'
            className={classnames('tInput__counter', {
              'tInput__counter--error': hasError,
              'tInput__counter--warning': counter.warning && !hasError,
            })}
          >
            {counter.label}
          </small>
        )}
        <div className='tInput__state'>
          {hasError && (
            <small id={`${props.name}-error`} role='alert' className='tInput__state__error'>
              {error}
            </small>
          )}
        </div>
      </div>
    </>
  )
}

export default Input
